import axios, { AxiosError, AxiosInstance } from 'axios';

export interface OrganizationStructureContent {
    id: number;
    image: string;
    created_at: string;
    updated_at: string;
}

export interface CreateOrganizationStructureData {
    image?: File;
}

export interface UpdateOrganizationStructureData {
    id: number;
    image?: File;
}

class OrganizationStructureService {
    private axiosInstance: AxiosInstance;

    constructor() {
        this.axiosInstance = axios.create({
            baseURL: process.env.REACT_APP_API_URL,
            timeout: 10000,
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });

        this.axiosInstance.interceptors.request.use(
            (config) => {
                const storageType = localStorage.getItem('storageType');
                const storage = storageType === 'local' ? localStorage : sessionStorage;
                const token = storage.getItem('token');
                if (token && config.headers) {
                    config.headers.Authorization = `Bearer ${token}`;
                }
                return config;
            },
            (error) => Promise.reject(error)
        );
    }

    async createOrganizationStructure(data: CreateOrganizationStructureData): Promise<OrganizationStructureContent | undefined> {
        try {
            const formData = new FormData();
            if (data.image) {
                formData.append('image', data.image);
            }

            const response = await this.axiosInstance.post<{
                message: string;
                data: OrganizationStructureContent;
            }>('/admin/os-content', formData);

            return response.data.data;
        } catch (error) {
            this.handleError(error, 'Failed to create organization structure content');
            return undefined;
        }
    }

    async getOrganizationStructures(): Promise<OrganizationStructureContent[]> {
        try {
            const response = await this.axiosInstance.get<{
                data: OrganizationStructureContent[];
            }>('/os-content');
            console.log(response.data.data);
            
            return response.data.data;
        } catch (error) {
            this.handleError(error, 'Failed to retrieve organization structure content');
            return [];
        }
    }

    async getOrganizationStructureById(id: number): Promise<OrganizationStructureContent | undefined> {
        try {
            const response = await this.axiosInstance.get<{
                data: OrganizationStructureContent;
            }>(`/os-content/${id}`);
            return response.data.data;
        } catch (error) {
            this.handleError(error, 'Failed to retrieve organization structure content');
            return undefined;
        }
    }

    async updateOrganizationStructure(data: UpdateOrganizationStructureData): Promise<OrganizationStructureContent | undefined> {
        try {
            const formData = new FormData();
            if (data.image) {
                formData.append('image', data.image);
            }

            const response = await this.axiosInstance.put<{
                message: string;
                data: OrganizationStructureContent;
            }>(`/admin/os-content/${data.id}`, formData);
            console.log(response.data.data);
            
            
            return response.data.data;
        } catch (error) {
            if (error instanceof AxiosError) {
                if (error.response?.status === 409) {
                    throw new Error("Conflict: An organization structure with this data already exists.");
                }
            }
            this.handleError(error, 'Failed to update organization structure content');
            return undefined;
        }
    }

    async deleteOrganizationStructure(id: number): Promise<void> {
        try {
            await this.axiosInstance.delete(`/admin/os-content/${id}`);
        } catch (error) {
            this.handleError(error, 'Failed to delete organization structure content');
        }
    }

    private handleError(error: unknown, message: string) {
        if (error instanceof AxiosError) {
            console.error(`${message}:`, error.response?.data);
            throw error.response?.data || { error: message };
        }
        console.error(message, error);
        throw { error: message };
    }
}

export const organizationStructureService = new OrganizationStructureService();